import { useParams } from "react-router-dom";
import { isErrorWithMessage, returnErrorMessage, showErrorMessages } from "../../app/api";
import { useEffect, useRef, useState } from "react";
import {
    IEventRead,
    IValidateTicketResponse,
    useLazyReadEventQuery,
    useValidateTicketMutation
} from "../../api/event";
import { Card, Flex, LoadingOverlay, Text } from "@mantine/core";
import QrReader from "../../components/qr-reader/QrReader";
import dayjs from "dayjs";
import { useLazyTicketStatisticsQuery } from "../../api/analytics";
import scanned_success_sound from './audio/scanned_success_sound.mp3'
import scanned_error_sound from './audio/scanned_error_sound.mp3'
import { useTranslation } from "react-i18next";
import './ticket-validation.css';
import React from "react";
import { useMediaQuery } from "@mantine/hooks";
import ScannedHistory from "./scanned-history";
import { IProgresBar } from "./ticket-validation.interface";
import SeatsProgressBar from "./seats-progress-bar";
import ScannedResultModal from "./modal/ScannedResultModal";

const audio_success = new Audio(scanned_success_sound);
const audio_error = new Audio(scanned_error_sound);

const TicketValidation = () => {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery('(max-width: 700px)');
    const isMaxHeight700 = useMediaQuery('(max-height: 700px)');

    const params = useParams();
    const cameraRef = useRef<{ preferredCamera: string }>();
    const [validateTicketRq] = useValidateTicketMutation();
    const [readEvent] = useLazyReadEventQuery();
    const [event, setEvent] = useState<IEventRead>()

    const [getStatistics] = useLazyTicketStatisticsQuery();

    // Result
    const [scannedResult, setScannedResult] = useState<string | undefined>("");
    const [scannedError, setScannedError] = useState<string | undefined | string[]>("");
    const [validatedResult, setValidatedResult] = useState<IValidateTicketResponse | undefined>();
    const [validatedResultArray, setValidatedResultArray] = useState<IValidateTicketResponse[] | undefined>();
    const [validatedToken, setValidatedToken] = useState<string | undefined>(undefined)
    const [seats, setSeats] = useState<IProgresBar>({ total: 0, used: 0, percentage: 0 })
    const [isPaused, setIsPaused] = useState<boolean>(false);

    useEffect(() => {
        if (params.id) {
            readEvent({ id: Number(params.id) }).then(res => setEvent(res.data))
            checkSeats()
        }
    }, [params.id]);

    useEffect(() => {
        if (scannedResult && params.id && !isPaused) {
            validateSeat(scannedResult, Number(params.id));
        }
    }, [scannedResult, params.id, validatedToken, isPaused])

    async function validateSeat(token: string, eventId: number) {
        const activeCamera = cameraRef.current?.preferredCamera;
        try {
            //do not validate previous scanned ticket
            if (validatedToken === token) return;

            //play sound success scanned qr code
            await audio_success.play();

            //set scan pause
            if (activeCamera) {
                setIsPaused(true)
            }

            //validate ticket on backend
            const res = await validateTicketRq({ event: eventId, token }).unwrap();

            //set validated ticket, set scanned token
            setValidatedToken(token)
            setValidatedResult(res)
            setScannedError('');

            //re-count stats
            checkSeats();
        } catch (e) {
            await audio_error.play(); //play error sound
            if (isErrorWithMessage(e)) {
                const errors = returnErrorMessage(e.data);
                if (activeCamera) {
                    setIsPaused(true)
                }
                setValidatedToken(token)
                setValidatedResult(undefined)
                setScannedError(errors)
            }
        }
    }

    async function checkSeats() {
        try {
            const statistics = await getStatistics({ event: Number(params.id) }).unwrap();
            const soldTickets = statistics.filter((f: any) => f.status === "sold").length;
            const usedSeats = statistics.filter((f: any) => f.status === "used").length;
            setSeats({
                total: soldTickets,
                used: usedSeats,
                percentage: (100 * (usedSeats)) / soldTickets
            })
        } catch (e) {
            if (isErrorWithMessage(e)) {
                showErrorMessages(e.data);
            }
        }
    }

    function confirmScannedResult() {
        setIsPaused(false);

        //modal animation delay
        setTimeout(() => {
            setValidatedResult(undefined)
            setScannedError('');
        }, 100)

        let maxArraySize = isMaxHeight700 ? 5 : 6;

        if (validatedResult) {
            setValidatedResultArray(prevArray => {
                const newArray = prevArray ? [validatedResult, ...prevArray] : [validatedResult];
                return newArray.length >= maxArraySize ? newArray.slice(0, maxArraySize) : newArray;
            });
        }
    }

    if (!event) return (
        <Flex
            gap={10}
            direction={{ base: 'column', xs: 'row', sm: "row", lg: 'row' }}
            h={'calc(100vh - 90px)'}
        >
            <Card
                w={{ sm: '100%', lg: "calc(100% - 300px)" }}
                h={isSmallScreen ? '256px' : '100%'}
            >
                <LoadingOverlay visible={true} />
            </Card>

            <Card
                w={{ sm: '100%', xs: "50%", lg: '300px' }}
                h={'100%'}
            >
                <LoadingOverlay visible={true} />
            </Card>
        </Flex>
    )

    return (
        <Flex
            gap={10}
            direction={{ base: 'column', xs: 'row', sm: "row", lg: 'row' }}
            h={'calc(100vh - 90px)'}
        >
            <Card
                w={{ sm: '100%', lg: "calc(100% - 300px)" }}
                h={{ sm: '90%', lg: '100%' }}
            >
                <QrReader
                    onScanSuccess={setScannedResult}
                    onScanFail={setScannedResult}
                    paused={isPaused}
                    cameraRef={cameraRef}
                />
            </Card>

            <Card
                w={{ sm: '100%', xs: "50%", lg: '300px' }}
                px={15}
                h={{ base: 'calc(100% - 300px)', xs: "100%", md: '100%' }}
            >
                <Flex
                    gap={10}
                    direction={{ base: 'column', sm: 'column', md: "column", lg: 'column' }}
                    justify={"space-between"}
                    h={'100%'}
                    w={'100%'}
                >
                    <Flex
                        direction={"column"}
                    >
                        <Text size={"lg"} fw={'bolder'}
                            style={{
                                overflow: "hidden",
                                display: '-webkit-box',
                                '-webkit-box-orient': 'vertical',
                                '-webkit-line-clamp': "2",
                                'line-clamp': 2
                            }}
                        >
                            {event.name}
                        </Text>

                        <Text size={"md"} pt={5}>{dayjs(event.start).format("DD MMMM YYYY HH:mm")}</Text>
                    </Flex>


                    <ScannedHistory
                        localHistory={validatedResultArray!}
                        t={t}
                    />

                    <SeatsProgressBar
                        seats={seats}
                        t={t}
                    />
                </Flex>
            </Card>

            <ScannedResultModal
                isPaused={isPaused}
                validatedResult={validatedResult}
                scannedError={scannedError}
                t={t}
                confirmScannedResult={confirmScannedResult}
            />
        </Flex>
    )
}

export default React.memo(TicketValidation);